/**
 * EEG–PACS multimodal co-review cockpit (Epic D3 / Gap #17 / CX-G12).
 * Drives live DICOM C-FIND / C-MOVE via systemapi; no demo UIDs in
 * staging/production. Local DEV may seed placeholders when no patientId.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import {
  MULTIMODAL_PHI_CONTROLS,
  associateEegEventToImaging,
  buildPacsCoregSession,
  buildTumorBoardExportPackage,
  linkImagingToEegStudy,
  resolveImagingViewerTarget,
  type EegImagingLinkBundle,
  type PacsCoregSession,
  type TumorBoardExportPackage,
} from "../utils/eegImagingLinkage";
import AsyncStudySelect, {
  type AsyncStudySelectOption,
} from "@shared/components/AsyncStudySelect";
import { allowClinicalPlaceholders } from "../utils/clinicalPlaceholders";
import {
  mapPacsStudiesToImagingRefs,
  queryPacsForPatient,
  retrievePacsStudy,
} from "../services/dicomPacsClient";

function loadDevOnlyDemoImaging() {
  if (!import.meta.env.DEV || !allowClinicalPlaceholders()) {
    return [] as ReturnType<typeof mapPacsStudiesToImagingRefs>;
  }
  return mapPacsStudiesToImagingRefs(
    [
      {
        studyInstanceUID: "1.2.840.demo.mri.1",
        seriesInstanceUID: "1.2.840.demo.mri.1.series.1",
        modality: "MR",
        studyDescription: "Brain MRI — epilepsy protocol (DEV only)",
        patientID: "pat-dev",
        accessionNumber: "ACC-DEV-9001",
      },
    ],
    "pat-dev",
  );
}

export default function ImagingCoReviewPanel() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorId = user?.id || "anonymous";

  const initialPatientId = searchParams.get("patientId") || "";
  const initialEegStudyId = searchParams.get("eegStudyId") || "";

  const [patientId, setPatientId] = useState(initialPatientId);
  const [patientLabel, setPatientLabel] = useState("");
  const [eegStudyId, setEegStudyId] = useState(
    initialEegStudyId || (allowClinicalPlaceholders() ? "eeg-study-dev" : ""),
  );
  const [eventTimeSeconds, setEventTimeSeconds] = useState(0);
  const [eventNote, setEventNote] = useState("EEG event");
  const [loading, setLoading] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [session, setSession] = useState<PacsCoregSession | null>(null);
  const [bundle, setBundle] = useState<EegImagingLinkBundle | null>(null);
  const [exportPkg, setExportPkg] = useState<TumorBoardExportPackage | null>(
    null,
  );
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [retrieveStatus, setRetrieveStatus] = useState<string | null>(null);
  const [usedDevPlaceholders, setUsedDevPlaceholders] = useState(false);

  const applyImaging = useCallback(
    (
      imaging: ReturnType<typeof mapPacsStudiesToImagingRefs>,
      opts: { patientId: string; eegStudyId: string; qidoReady: boolean },
    ) => {
      const nextSession = buildPacsCoregSession({
        eegStudyId: opts.eegStudyId,
        patientId: opts.patientId,
        imaging,
        actorId,
        dicomweb: {
          qidoReady: opts.qidoReady,
          wadoReady: opts.qidoReady,
          stowReady: false,
        },
      });
      setSession(nextSession);
      setBundle(nextSession.bundle);
      setViewerUrl(null);
      setRetrieveStatus(null);
      setExportPkg(null);
    },
    [actorId],
  );

  const runPacsQuery = useCallback(
    async (pid: string, studyId: string) => {
      const trimmed = pid.trim();
      if (!trimmed) {
        setQueryError("patientId is required for live PACS C-FIND");
        if (allowClinicalPlaceholders()) {
          const demo = loadDevOnlyDemoImaging();
          setUsedDevPlaceholders(true);
          applyImaging(demo, {
            patientId: "pat-dev",
            eegStudyId: studyId || "eeg-study-dev",
            qidoReady: false,
          });
        } else {
          setSession(null);
          setBundle(null);
        }
        return;
      }

      setLoading(true);
      setQueryError(null);
      setUsedDevPlaceholders(false);
      try {
        const { studies, error } = await queryPacsForPatient(trimmed);
        if (error) {
          setQueryError(error);
        }
        const imaging = mapPacsStudiesToImagingRefs(studies, trimmed);
        applyImaging(imaging, {
          patientId: trimmed,
          eegStudyId: studyId || `eeg-${trimmed}`,
          qidoReady: !error,
        });
        if (!imaging.length && !error) {
          setQueryError("No imaging studies returned from PACS for this patient");
        }
      } finally {
        setLoading(false);
      }
    },
    [applyImaging],
  );

  useEffect(() => {
    if (initialPatientId) {
      void runPacsQuery(initialPatientId, initialEegStudyId);
    } else if (allowClinicalPlaceholders()) {
      const demo = loadDevOnlyDemoImaging();
      setUsedDevPlaceholders(true);
      applyImaging(demo, {
        patientId: "pat-dev",
        eegStudyId: eegStudyId || "eeg-study-dev",
        qidoReady: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once from URL
  }, []);

  const selectedImaging = bundle?.imaging[0];

  const associateSelected = () => {
    if (!bundle || !selectedImaging) return;
    setBundle((prev) => {
      if (!prev) return prev;
      return associateEegEventToImaging(
        prev,
        {
          eventId: `evt-${Date.now()}`,
          timeSeconds: eventTimeSeconds,
          note: eventNote,
        },
        {
          studyInstanceUID: selectedImaging.studyInstanceUID,
          seriesInstanceUID: selectedImaging.seriesInstanceUID,
          frameNumber: null,
        },
        actorId,
      );
    });
  };

  const openAssociation = async (associationId: string) => {
    if (!bundle) return;
    const target = resolveImagingViewerTarget(bundle, associationId);
    setViewerUrl(target.viewerUrl);
    if (target.association?.studyInstanceUID) {
      setRetrieveStatus("Retrieving study from PACS (C-MOVE)…");
      const result = await retrievePacsStudy(
        target.association.studyInstanceUID,
      );
      if (result.ok) {
        setRetrieveStatus(
          `Retrieved ${result.fileCount} file(s) via C-MOVE. Open viewer URL for slice navigation.`,
        );
      } else {
        setRetrieveStatus(
          `C-MOVE unavailable or failed: ${result.error}. Viewer URL still resolved from QIDO linkage.`,
        );
      }
    }
  };

  const exportPackage = () => {
    if (!bundle) return;
    setExportPkg(
      buildTumorBoardExportPackage({
        bundle,
        eegSnapshotRefs: bundle.associations.map((a, i) => ({
          evidenceId: `snap-${i + 1}`,
          label: a.note || a.eegEventId,
          timeSeconds: a.timeSeconds,
        })),
        actorId,
      }),
    );
  };

  const onSubmitQuery = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (patientId.trim()) next.set("patientId", patientId.trim());
    else next.delete("patientId");
    if (eegStudyId.trim()) next.set("eegStudyId", eegStudyId.trim());
    else next.delete("eegStudyId");
    setSearchParams(next);
    void runPacsQuery(patientId, eegStudyId);
  };

  const auditTail = useMemo(
    () => (bundle?.accessAudit || []).slice(-8).reverse(),
    [bundle?.accessAudit],
  );

  return (
    <Container fluid className="py-3" data-testid="imaging-co-review-panel">
      <Alert variant="secondary" className="mb-3">
        Multimodal PHI controls: retention{" "}
        {MULTIMODAL_PHI_CONTROLS.retentionDaysConferenceExport}d conference
        exports · risk {MULTIMODAL_PHI_CONTROLS.riskId} · patient-match required.
        Live PACS C-FIND / C-MOVE via <code>/api/dicom/*</code>.
      </Alert>

      {usedDevPlaceholders ? (
        <Alert variant="warning" className="mb-3" data-testid="imaging-dev-placeholders">
          Local-dev placeholder imaging is active. Staging and production builds
          require a real <code>patientId</code> and live PACS query results.
        </Alert>
      ) : null}

      <Card className="mb-3">
        <Card.Header>PACS query (C-FIND)</Card.Header>
        <Card.Body>
          <Form onSubmit={onSubmitQuery}>
            <div className="row g-3 align-items-end">
              <Form.Group
                controlId="imaging-study-select"
                className="col-lg-5 col-md-12"
              >
                <Form.Label>Patient / EEG study</Form.Label>
                <AsyncStudySelect
                  value={eegStudyId === "eeg-study-dev" ? "" : eegStudyId}
                  valueKind="study"
                  placeholder="Search patient, MRN, or study…"
                  clearLabel="Clear selected study"
                  onChange={(studyId) => {
                    setEegStudyId(studyId);
                  }}
                  onOptionChange={(option: AsyncStudySelectOption | null) => {
                    if (!option) {
                      setPatientId("");
                      setPatientLabel("");
                      return;
                    }
                    const pacsPatientId =
                      (option.mrn || "").trim() || option.study_id;
                    setPatientId(pacsPatientId);
                    setPatientLabel(option.label);
                    setEegStudyId(option.study_id);
                  }}
                />
              </Form.Group>
              <Form.Group
                controlId="imaging-patient-id"
                className="col-lg-3 col-md-6"
              >
                <Form.Label>PACS Patient ID</Form.Label>
                <Form.Control
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  placeholder="MRN / PatientID for C-FIND"
                  data-testid="imaging-patient-id"
                />
              </Form.Group>
              <Form.Group
                controlId="imaging-eeg-study-id"
                className="col-lg-4 col-md-6"
              >
                <Form.Label>EEG study ID</Form.Label>
                <Form.Control
                  value={eegStudyId === "eeg-study-dev" ? "" : eegStudyId}
                  onChange={(e) => setEegStudyId(e.target.value)}
                  placeholder="Linked EEG study"
                  data-testid="imaging-eeg-study-id"
                  readOnly
                />
              </Form.Group>
            </div>
            <Form.Text
              className="text-muted d-block mt-2"
              data-testid="imaging-selected-context"
            >
              {patientId
                ? `PACS Patient ID: ${patientId}${patientLabel ? ` · ${patientLabel}` : ""}`
                : "Select a portal study to populate PACS Patient ID (MRN) and EEG study."}
            </Form.Text>
            <div className="mt-3">
              <Button
                type="submit"
                disabled={loading}
                data-testid="imaging-pacs-query"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Querying…
                  </>
                ) : (
                  "Query PACS"
                )}
              </Button>
            </div>
          </Form>
          {queryError ? (
            <Alert variant="danger" className="mt-3 mb-0" data-testid="imaging-query-error">
              {queryError}
            </Alert>
          ) : null}
          {session ? (
            <div className="mt-3 small text-muted" data-testid="imaging-coreg-session">
              Co-reg session {session.sessionId} · QIDO{" "}
              {session.dicomweb.qidoReady ? "ready" : "not ready"} · ESI{" "}
              {session.readyForEsi ? "ready" : "not ready"}
              {session.warnings.length
                ? ` · warnings: ${session.warnings.join(", ")}`
                : ""}
            </div>
          ) : null}
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Linked ImagingStudy / DICOM refs</Card.Header>
            <ListGroup variant="flush" data-testid="imaging-study-list">
              {(bundle?.imaging || []).map((img) => (
                <ListGroup.Item key={img.studyInstanceUID}>
                  <Badge bg="info" className="me-2">
                    {img.modality || "IMG"}
                  </Badge>
                  {img.description || img.studyInstanceUID}
                  <div>
                    <small className="text-muted">
                      {img.source} · {img.studyInstanceUID}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
              {!bundle?.imaging?.length ? (
                <ListGroup.Item className="text-muted">
                  No imaging linked. Query PACS with a patient ID.
                </ListGroup.Item>
              ) : null}
            </ListGroup>
            {bundle && !bundle.patientMatchOk ? (
              <Alert variant="danger" className="m-2 mb-2">
                {bundle.patientMatchWarning}
              </Alert>
            ) : null}
          </Card>

          <Card>
            <Card.Header>EEG event ↔ imaging association</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Event time (seconds)</Form.Label>
                <Form.Control
                  type="number"
                  value={eventTimeSeconds}
                  onChange={(e) =>
                    setEventTimeSeconds(Number(e.target.value) || 0)
                  }
                  data-testid="imaging-event-time"
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Note</Form.Label>
                <Form.Control
                  value={eventNote}
                  onChange={(e) => setEventNote(e.target.value)}
                  data-testid="imaging-event-note"
                />
              </Form.Group>
              <Button
                className="me-2 mb-2"
                onClick={associateSelected}
                disabled={!selectedImaging}
                data-testid="imaging-associate"
              >
                Link event → selected study
              </Button>
              <Button
                className="mb-2"
                variant="outline-primary"
                onClick={exportPackage}
                disabled={!bundle?.associations.length}
                data-testid="imaging-tumor-board-export"
              >
                Tumor board export
              </Button>
              <ListGroup data-testid="imaging-associations">
                {(bundle?.associations || []).map((a) => (
                  <ListGroup.Item
                    key={a.associationId}
                    action
                    onClick={() => void openAssociation(a.associationId)}
                  >
                    {a.eegEventId} @ {a.timeSeconds}s → {a.studyInstanceUID}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {viewerUrl ? (
                <Alert
                  variant="info"
                  className="mt-2 mb-0"
                  data-testid="imaging-viewer-target"
                >
                  Viewer:{" "}
                  <a href={viewerUrl} target="_blank" rel="noreferrer">
                    {viewerUrl}
                  </a>
                </Alert>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Imaging viewport</Card.Header>
            <Card.Body
              className="bg-dark text-light text-center py-5"
              data-testid="imaging-viewport"
            >
              {viewerUrl ? (
                <div>
                  <div className="mb-2">Linked study ready</div>
                  <a
                    className="btn btn-outline-light btn-sm"
                    href={viewerUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="imaging-viewer-open"
                  >
                    Open DICOM viewer target
                  </a>
                  {retrieveStatus ? (
                    <div className="small mt-3 text-white-50">{retrieveStatus}</div>
                  ) : null}
                </div>
              ) : (
                "Select an association to resolve viewer URL and trigger C-MOVE"
              )}
            </Card.Body>
          </Card>
          <Card className="mb-3">
            <Card.Header>Access audit</Card.Header>
            <ListGroup variant="flush" data-testid="imaging-access-audit">
              {auditTail.map((entry, i) => (
                <ListGroup.Item key={`${entry.atIso}-${i}`} className="small">
                  <Badge bg="dark" className="me-2">
                    {entry.action}
                  </Badge>
                  {entry.studyInstanceUID || entry.eegStudyId}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
          {exportPkg ? (
            <Card data-testid="tumor-board-package">
              <Card.Header>Conference package {exportPkg.packageId}</Card.Header>
              <Card.Body className="small">
                EEG snaps: {exportPkg.eegSnapshotRefs.length} · Imaging refs:{" "}
                {exportPkg.imagingRefs.length} · Retention:{" "}
                {exportPkg.retentionClass}
              </Card.Body>
            </Card>
          ) : null}
        </Col>
      </Row>
    </Container>
  );
}

/** Test helper: build an empty live bundle without demo UIDs. */
export function buildEmptyLiveBundle(input: {
  eegStudyId: string;
  patientId: string;
  actorId: string;
}): EegImagingLinkBundle {
  return linkImagingToEegStudy({
    eegStudyId: input.eegStudyId,
    patientId: input.patientId,
    imaging: [],
    actorId: input.actorId,
  });
}
